import { useRef, useState } from 'react';
import { MarketTabs, panelId, tabId } from '@/components/market-tabs';
import { PaymentMethods } from '@/components/payment-methods';
import { Reveal } from '@/components/reveal';
import { Section, shellClass } from '@/components/section';
import { SectionHeader, playHeader } from '@/components/section-header';
import { copy } from '@/content/copy';
import { MARKET_COUNT, METHOD_COUNT, marketKinds, marketMethodCount } from '@/content/facts';
import { DIRECTIONS, markets, type Direction } from '@/content/payments';
import { cn } from '@/lib/cn';
import { useRevealOnEnter } from '@/scroll/use-reveal-on-enter';

const { coverage } = copy;
const TABS_ID = 'coverage-markets';
// Module-level so the strip's scroll-into-view effect only fires when the selection changes.
const TABS = markets.map((m) => ({ id: m.id, label: m.name }));

/**
 * The payment-method schedule, one market at a time. The tab strip picks the market; its
 * panel shows the banks, wallets and networks it takes, with the logos supplied for each.
 * Every market takes both directions on the same methods, so the segmented control does not
 * swap the panel; it switches the facts strip between the deposit and payout minimum and
 * names the direction in the panel heading.
 */
export function Coverage() {
  const ref = useRef<HTMLElement>(null);
  const [direction, setDirection] = useState<Direction>('deposit');
  const [marketId, setMarketId] = useState(markets[0].id);
  const market = markets.find((m) => m.id === marketId) ?? markets[0];

  useRevealOnEnter(ref, (tl, q) => {
    const at = playHeader(tl, q);
    tl.to(q('[data-summary]'), { opacity: 1, y: 0, duration: 0.6 }, at - 0.15)
      .to(q('[data-control]'), { opacity: 1, y: 0, duration: 0.6 }, at - 0.1)
      .to(q('[data-tabs]'), { opacity: 1, y: 0, duration: 0.6 }, at)
      .to(q('[data-facts]'), { opacity: 1, y: 0, duration: 0.6 }, at + 0.15)
      .to(q('[data-panel]'), { opacity: 1, y: 0, duration: 0.7 }, at + 0.3)
      .to(q('[data-note]'), { opacity: 1, y: 0, duration: 0.6 }, at + 0.7);
  });

  // Everything here is the schedule's: what the market takes, and its minimum for the direction.
  const facts = [
    { label: coverage.facts.methods, value: String(marketMethodCount(market)) },
    {
      label: coverage.facts.rails,
      value: marketKinds(market)
        .map((kind) => coverage.kinds[kind])
        .join(' · '),
    },
    {
      label: coverage.modes[direction].minimum,
      value: `${market.minimum[direction]} ${market.currency}`,
    },
  ];

  return (
    <Section ref={ref} id="coverage">
      <div className={cn(shellClass, 'grid gap-12')}>
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="grid gap-5">
            <SectionHeader eyebrow={coverage.eyebrow} title={coverage.title} lede={coverage.lede} />
            <Reveal as="p" data-summary className="m-0 text-sm font-medium text-brand-sky">
              {coverage.summary(MARKET_COUNT, METHOD_COUNT)}
            </Reveal>
          </div>
          <Reveal data-control className="justify-self-start lg:justify-self-end">
            <div
              role="group"
              aria-label={coverage.modes.label}
              className="relative inline-grid grid-cols-2 rounded-full border border-border bg-background/60 p-1"
            >
              {/* The indicator slides; the buttons only change colour. */}
              <span
                aria-hidden
                className={cn(
                  'absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-primary duration-500 ease-out-expo motion-safe:transition-transform',
                  direction === 'payout' && 'translate-x-full',
                )}
              />
              {DIRECTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={direction === option}
                  onClick={() => setDirection(option)}
                  className={cn(
                    'relative z-10 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    direction === option
                      ? 'text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {coverage.modes[option].tab}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        {/* min-w-0 on the grid items: the tab strip's intrinsic width would otherwise size the
            column and push the whole shell past a narrow viewport. */}
        <div className="grid gap-8 *:min-w-0">
          <Reveal data-tabs>
            <MarketTabs
              tabs={TABS}
              activeId={market.id}
              onSelect={setMarketId}
              idPrefix={TABS_ID}
              label={coverage.markets.label}
            />
          </Reveal>

          <Reveal
            as="div"
            data-facts
            className="grid gap-6 sm:grid-cols-[auto_1fr] sm:items-center sm:gap-10"
          >
            <span className="flex items-center gap-3">
              <span
                aria-hidden
                className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/15 font-mono text-[0.72rem] font-semibold text-brand-sky"
              >
                {market.code}
              </span>
              <span className="grid gap-0.5">
                <span className="text-base font-semibold leading-tight">{market.name}</span>
                <span className="hud text-[0.58rem] text-muted-foreground">
                  {coverage.modes[direction].row}
                </span>
              </span>
            </span>
            {/* Polite: the toggle changes the minimum without moving focus. */}
            <dl
              aria-live="polite"
              aria-atomic="true"
              className="m-0 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-border"
            >
              {facts.map((fact) => (
                <div key={fact.label} className="grid gap-1 bg-card/70 px-4 py-3">
                  <dt className="hud text-[0.58rem] text-muted-foreground">{fact.label}</dt>
                  <dd className="m-0 text-sm font-semibold leading-tight">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          {/* The wrapper is the GSAP reveal target; the keyed child replays a CSS entrance on
              every market change so the two motions never share an element. */}
          <Reveal data-panel>
            <div
              key={market.id}
              role="tabpanel"
              // Nothing inside is focusable, so the panel itself takes a tab stop.
              tabIndex={0}
              id={panelId(TABS_ID, market.id)}
              aria-labelledby={tabId(TABS_ID, market.id)}
              className="rounded-xl motion-safe:animate-panel-enter focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <PaymentMethods market={market} />
            </div>
          </Reveal>
        </div>

        <Reveal
          as="p"
          data-note
          className="m-0 max-w-[720px] text-xs leading-relaxed text-muted-foreground/80"
        >
          {coverage.note}
        </Reveal>
      </div>
    </Section>
  );
}
